import { ENTITY_TYPE } from '../../constants'

/**
 * One record per (entity, type) holding that entity's whole transaction array —
 * keyed by `${entityId}_${entityType}`. Storing one record per account instead of
 * one per transaction is ~40x fewer IndexedDB writes on a large seed.
 */
export interface IEntityTransactionsRecord {
  entityId_entityType: string
  entityId: string
  entityType: ENTITY_TYPE
  transactions: any[]
  lastUpdated: number
  /**
   * Owning business. The store is shared by every business the user owns (the seed
   * background-seeds them all), so the delta cursor must be able to tell them apart.
   * Optional: records written before this field existed are untagged.
   */
  businessId?: string
}

export interface ITransactionMetadata {
  entityId: string
  entityType: ENTITY_TYPE
  lastRecentSync: number
  lastHistoricalSync: number
  totalTransactions: number
  recentTransactionCount: number
  historicalTransactionCount: number
  lastTransactionTimestamp: number
  hasCompletedFullFetch: boolean
  createdAt: number
  updatedAt: number
}

export interface ISeedInfo {
  businessId: string
  seedVersion: number
  seededAt: number
  /** account_types finished so far — the resume checkpoint for an interrupted seed. */
  completedAccountTypes?: number[]
}

class TransactionDatabaseManager {
  // Upper bound on how long init() waits for the IndexedDB open/upgrade to settle.
  // A blocked upgrade (another tab holds an older-version connection open) would
  // otherwise leave the promise pending forever and deadlock every caller.
  private static readonly INIT_TIMEOUT_MS = 10000
  private db: IDBDatabase | null = null
  private readonly dbName = 'TransactionDB'
  // v6: `transactions` holds ONE record per (entity, type) — the entity's whole
  // txn array — instead of one record per transaction. ~40x fewer writes on a
  // large seed. The upgrade recreates the store (old per-txn cache is dropped and
  // re-fetched) and resets metadata/seed so everything re-populates in the new shape.
  private readonly version = 6
  private readonly transactionStoreName = 'transactions'
  private readonly metadataStoreName = 'transaction_metadata'
  private readonly seedMetadataStoreName = 'business_seed_metadata'

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        reject(new Error('IndexedDB is not supported'))
        return
      }

      const request = indexedDB.open(this.dbName, this.version)

      // A blocked upgrade (another tab holds an OLDER-version connection open) must
      // never leave this promise pending forever. Previously `onblocked` only logged,
      // so `init()` could hang indefinitely — and every caller that awaits it (seed,
      // Refresh, ledger reads) deadlocked behind it: infinite spinner, empty ledger,
      // no network calls, no recovery. A watchdog guarantees init() always settles.
      let settled = false
      const settle = (fn: () => void) => {
        if (settled) {
          return
        }
        settled = true
        clearTimeout(watchdog)
        fn()
      }
      const watchdog = setTimeout(() => {
        settle(() =>
          reject(
            new Error(
              'IndexedDB open timed out — upgrade likely blocked by another open OkCredit tab'
            )
          )
        )
      }, TransactionDatabaseManager.INIT_TIMEOUT_MS)

      request.onerror = () => settle(() => reject(new Error('Failed to open IndexedDB')))

      // Log the block but do NOT reject yet: if the other tab closes its connection,
      // `onsuccess`/`onupgradeneeded` still fire and we resolve normally. The watchdog
      // rejects only if the block never clears, so transient blocks self-heal.
      request.onblocked = () => {
        console.warn('[DBManager] IndexedDB open blocked by another tab (older version open)')
      }

      request.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        this.db = db
        // If another tab needs a higher version, close this connection so its
        // upgrade proceeds instead of hanging (the other tab's `onblocked`).
        db.onversionchange = () => {
          db.close()
          this.db = null
          console.warn(
            '[DBManager] Closed IndexedDB connection for a version change in another tab'
          )
        }
        console.log('[DBManager] Database opened successfully')
        settle(() => resolve())
      }

      request.onupgradeneeded = (event) => {
        const target = event.target as IDBOpenDBRequest
        const db = target.result
        const oldVersion = event.oldVersion
        const newVersion = event.newVersion

        console.log('[DBManager] Upgrading database from version', oldVersion, 'to', newVersion)

        // transactions: one record per (entity, type), keyed by entityId_entityType,
        // no indexes (looked up directly by key). Recreate on upgrade — the old
        // per-txn layout is incompatible; cached txns re-populate via seed / lazy fetch.
        if (db.objectStoreNames.contains(this.transactionStoreName)) {
          db.deleteObjectStore(this.transactionStoreName)
        }
        db.createObjectStore(this.transactionStoreName, { keyPath: 'entityId_entityType' })

        // metadata: recreate empty so hasCompletedFullFetch resets (forces a re-fetch
        // that repopulates the new store shape). No indexes — none are queried.
        if (db.objectStoreNames.contains(this.metadataStoreName)) {
          db.deleteObjectStore(this.metadataStoreName)
        }
        db.createObjectStore(this.metadataStoreName, { keyPath: 'entityId_entityType' })

        // seed flags: recreate empty so businesses re-seed into the new store shape.
        if (db.objectStoreNames.contains(this.seedMetadataStoreName)) {
          db.deleteObjectStore(this.seedMetadataStoreName)
        }
        db.createObjectStore(this.seedMetadataStoreName, { keyPath: 'businessId' })
      }
    })
  }

  private entityKey(entityId: string, entityType: ENTITY_TYPE): string {
    return `${entityId}_${entityType}`
  }

  // Transaction methods

  /** Replace an entity's cached transactions with `transactions` (one put). */
  async storeTransactions(
    entityId: string,
    entityType: ENTITY_TYPE,
    transactions: any[],
    businessId?: string
  ): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    if (transactions.length === 0) {
      return
    }

    const record: IEntityTransactionsRecord = {
      entityId_entityType: this.entityKey(entityId, entityType),
      entityId,
      entityType,
      transactions,
      lastUpdated: Date.now(),
      ...(businessId ? { businessId } : {})
    }
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readwrite')
      transaction.oncomplete = () => resolve()
      transaction.onabort = () => reject(transaction.error || new Error('transaction aborted'))
      transaction.onerror = () => reject(transaction.error)
      transaction.objectStore(this.transactionStoreName).put(record)
    })
  }

  /**
   * Store transactions for MANY accounts at once (entity = each row's `account_id`).
   * Groups by account and writes ONE record per account (its full array), batched
   * across a few IndexedDB transactions — a large seed needs ~(accounts / batch)
   * writes instead of one per transaction (~40x faster).
   */
  async bulkStoreTransactions(
    entityType: ENTITY_TYPE,
    transactions: any[],
    businessId?: string
  ): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    const byAccount = new Map<string, any[]>()
    for (const txn of transactions) {
      const accountId: string = txn?.account_id
      if (!accountId) {
        continue
      }
      const existing = byAccount.get(accountId)
      if (existing) {
        existing.push(txn)
      } else {
        byAccount.set(accountId, [txn])
      }
    }

    const entries = Array.from(byAccount.entries())
    const accountsPerTransaction = 500
    const now = Date.now()
    for (let start = 0; start < entries.length; start += accountsPerTransaction) {
      const batch = entries.slice(start, start + accountsPerTransaction)
      await new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([this.transactionStoreName], 'readwrite')
        const store = transaction.objectStore(this.transactionStoreName)
        transaction.oncomplete = () => resolve()
        transaction.onabort = () => reject(transaction.error || new Error('transaction aborted'))
        transaction.onerror = () => reject(transaction.error)

        for (const [accountId, accountTxns] of batch) {
          const record: IEntityTransactionsRecord = {
            entityId_entityType: this.entityKey(accountId, entityType),
            entityId: accountId,
            entityType,
            transactions: accountTxns,
            lastUpdated: now,
            ...(businessId ? { businessId } : {})
          }
          store.put(record)
        }
      })
      // Yield to the event loop between chunks so a very large seed doesn't
      // monopolise the main thread (keeps the tab responsive).
      if (start + accountsPerTransaction < entries.length) {
        await new Promise<void>((r) => setTimeout(r, 0))
      }
    }
  }

  /**
   * MERGE a delta of transactions (from an incremental `type:0` refresh) into the
   * cache without dropping history. Unlike `bulkStoreTransactions`, which REPLACES
   * each account's whole array, this reads each affected account's existing array
   * and upserts the delta rows by `id` (delta wins — an edited or soft-deleted row
   * overwrites the stored one in place). Accounts with no prior cache are created.
   *
   * Grouped by `account_id` and batched like `bulkStoreTransactions` (read + write
   * per account inside one readwrite transaction per batch). Untouched accounts are
   * never rewritten. Returns the number of rows that GENUINELY changed — a new `id`,
   * or an existing `id` whose `update_time` differs. Rows re-fetched identically
   * (the `include_start_time` boundary row) are still written but NOT counted, so
   * the refresh can report "up to date" instead of an inflated count.
   */
  async bulkUpsertTransactions(
    entityType: ENTITY_TYPE,
    transactions: any[],
    businessId?: string
  ): Promise<number> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    const byAccount = new Map<string, any[]>()
    for (const txn of transactions) {
      const accountId: string = txn?.account_id
      if (!accountId) {
        continue
      }
      const existing = byAccount.get(accountId)
      if (existing) {
        existing.push(txn)
      } else {
        byAccount.set(accountId, [txn])
      }
    }

    const entries = Array.from(byAccount.entries())
    const accountsPerTransaction = 500
    // Mutated inside the IndexedDB callbacks (which need the cached row to compare) —
    // held in an object so `mergeAccountInto`, defined once below, can bump it without
    // a loop-declared closure. All onsuccess handlers run before each batch
    // transaction's `oncomplete`, so the count is final when the batch promise resolves.
    const changed = { count: 0 }
    const mergeAccountInto = (
      store: IDBObjectStore,
      accountId: string,
      deltaTxns: any[],
      now: number
    ) => {
      const key = this.entityKey(accountId, entityType)
      // Read-modify-write inside the same transaction so concurrent batches
      // (or tabs) don't clobber each other's merge.
      const getReq = store.get(key)
      getReq.onsuccess = () => {
        const record = getReq.result as IEntityTransactionsRecord | undefined
        const merged = new Map<string, any>()
        if (record && Array.isArray(record.transactions)) {
          for (const t of record.transactions) {
            merged.set(t.id, t)
          }
        }
        for (const t of deltaTxns) {
          const prev = merged.get(t.id)
          // New id, or a real update (server bumps update_time on any change).
          // Same id + same update_time = the idempotent boundary re-fetch → not a change.
          if (!prev || Number(prev.update_time) !== Number(t.update_time)) {
            changed.count += 1
          }
          merged.set(t.id, t)
        }
        store.put({
          entityId_entityType: key,
          entityId: accountId,
          entityType,
          transactions: Array.from(merged.values()),
          lastUpdated: now,
          // Keep an existing tag when the caller doesn't know the business.
          ...(businessId || record?.businessId
            ? { businessId: businessId || record?.businessId }
            : {})
        })
      }
    }

    for (let start = 0; start < entries.length; start += accountsPerTransaction) {
      const batch = entries.slice(start, start + accountsPerTransaction)
      const now = Date.now()
      await new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([this.transactionStoreName], 'readwrite')
        const store = transaction.objectStore(this.transactionStoreName)
        transaction.oncomplete = () => resolve()
        transaction.onabort = () => reject(transaction.error || new Error('transaction aborted'))
        transaction.onerror = () => reject(transaction.error)

        for (const [accountId, deltaTxns] of batch) {
          mergeAccountInto(store, accountId, deltaTxns, now)
        }
      })
      if (start + accountsPerTransaction < entries.length) {
        await new Promise<void>((r) => setTimeout(r, 0))
      }
    }
    return changed.count
  }

  async getTransactionsByEntity(entityId: string, entityType: ENTITY_TYPE): Promise<any[]> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readonly')
      const store = transaction.objectStore(this.transactionStoreName)
      const request = store.get(this.entityKey(entityId, entityType))

      request.onsuccess = () => {
        const record = request.result as IEntityTransactionsRecord | undefined
        resolve(record && Array.isArray(record.transactions) ? record.transactions : [])
      }
      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  /**
   * Bulk read: every cached account's transactions for `entityType`, grouped by
   * `entityId`. One IndexedDB scan instead of a `getTransactionsByEntity` call per
   * account — powers the consolidated account report, which reads straight from
   * cache. Accounts with no cached transactions are simply absent from the map.
   */
  async getTransactionsByType(entityType: ENTITY_TYPE): Promise<Record<string, any[]>> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readonly')
      const store = transaction.objectStore(this.transactionStoreName)
      const request = store.openCursor()
      const grouped: Record<string, any[]> = {}

      request.onsuccess = () => {
        const cursor = request.result as IDBCursorWithValue | null
        if (!cursor) {
          resolve(grouped)
          return
        }
        const record = cursor.value as IEntityTransactionsRecord | undefined
        if (record && record.entityType === entityType && Array.isArray(record.transactions)) {
          grouped[record.entityId] = record.transactions
        }
        cursor.continue()
      }
      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  async hasData(entityId: string, entityType: ENTITY_TYPE): Promise<boolean> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readonly')
      const store = transaction.objectStore(this.transactionStoreName)
      const request = store.get(this.entityKey(entityId, entityType))

      request.onsuccess = () => {
        const record = request.result as IEntityTransactionsRecord | undefined
        resolve(!!(record && Array.isArray(record.transactions) && record.transactions.length > 0))
      }
      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  async getLastUpdatedTime(entityId: string, entityType: ENTITY_TYPE): Promise<number | null> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readonly')
      const store = transaction.objectStore(this.transactionStoreName)
      const request = store.get(this.entityKey(entityId, entityType))

      request.onsuccess = () => {
        const record = request.result as IEntityTransactionsRecord | undefined
        resolve(record ? record.lastUpdated : null)
      }
      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  /**
   * Max SERVER update time (unix seconds) across the stored transaction payloads
   * for an entity — read from each raw payload's `update_time` field, NOT the
   * store's local-write `lastUpdated`. Used as the incremental delta cursor after
   * a seed (mirrors Android). Returns null when the entity has no cached data.
   */
  async getMaxServerUpdateTime(entityId: string, entityType: ENTITY_TYPE): Promise<number | null> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readonly')
      const store = transaction.objectStore(this.transactionStoreName)
      const request = store.get(this.entityKey(entityId, entityType))

      request.onsuccess = () => {
        const record = request.result as IEntityTransactionsRecord | undefined
        const txns = record && Array.isArray(record.transactions) ? record.transactions : []
        let max = 0
        // Same guard as the business cursor: ignore values that can't be server seconds.
        const latestPlausible = Math.floor(Date.now() / 1000) + 24 * 60 * 60
        for (const txn of txns) {
          const updateTime = Number(txn?.update_time)
          if (Number.isFinite(updateTime) && updateTime <= latestPlausible && updateTime > max) {
            max = updateTime
          }
        }
        resolve(max > 0 ? max : null)
      }
      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  /**
   * Business-wide delta cursor: the max SERVER `update_time` (epoch seconds) across
   * EVERY cached account of `entityType`, not just one entity. Cursors over the
   * whole `transactions` store, filters records to `entityType`, and returns the
   * highest `update_time` found (0 when nothing is cached).
   *
   * This is the `start_time` for the `type:0` merchant-level refresh — one value
   * per business per account type. Self-advancing: the delta's newly-upserted rows
   * carry newer `update_time`, so the next call reads a higher cursor.
   *
   * Pass `businessId` to skip records tagged with ANOTHER business: the store holds
   * every owned business, and a cursor taken across all of them starts this
   * business's delta at the other one's newest change — everything in between is
   * then skipped for good (the cursor only moves forward). Untagged (older) records
   * still count. Values that can't be server epoch seconds (milliseconds, or a
   * client clock ahead of now) are ignored so one bad row can't freeze the sync.
   */
  async getBusinessMaxServerUpdateTime(
    entityType: ENTITY_TYPE,
    businessId?: string
  ): Promise<number> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readonly')
      const store = transaction.objectStore(this.transactionStoreName)
      const request = store.openCursor()
      let max = 0
      const latestPlausible = Math.floor(Date.now() / 1000) + 24 * 60 * 60

      request.onsuccess = () => {
        const cursor = request.result as IDBCursorWithValue | null
        if (!cursor) {
          resolve(max)
          return
        }
        const record = cursor.value as IEntityTransactionsRecord | undefined
        const otherBusiness =
          !!businessId && !!record?.businessId && record.businessId !== businessId
        if (
          record &&
          !otherBusiness &&
          record.entityType === entityType &&
          Array.isArray(record.transactions)
        ) {
          for (const txn of record.transactions) {
            const updateTime = Number(txn?.update_time)
            if (Number.isFinite(updateTime) && updateTime <= latestPlausible && updateTime > max) {
              max = updateTime
            }
          }
        }
        cursor.continue()
      }
      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  /**
   * Returns the set of (entityId, entityType) pairs we have cached transactions
   * for. Walks the metadata store (one row per entity).
   */
  async getAllCachedEntityIds(): Promise<Array<{ entityId: string; entityType: ENTITY_TYPE }>> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    if (!this.db.objectStoreNames.contains(this.metadataStoreName)) {
      return []
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.metadataStoreName], 'readonly')
      const store = transaction.objectStore(this.metadataStoreName)
      const request = store.getAll()

      request.onsuccess = () => {
        const results = request.result as ITransactionMetadata[]
        resolve(
          results
            .filter((r) => r && r.entityId && r.entityType !== undefined)
            .map((r) => ({ entityId: r.entityId, entityType: r.entityType }))
        )
      }
      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  /**
   * Soft-delete a single cached transaction in place (write-through) — mark it
   * `deleted` with a `delete_time` instead of dropping the whole account's cache.
   * Deliberately does NOT touch `update_time`: that field is the delta cursor
   * (`getBusinessMaxServerUpdateTime`), so advancing it to a client clock could
   * make the next sync skip server changes. The server assigns the real
   * `update_time`, and a later delta overwrites this row with the authoritative one.
   */
  async softDeleteCachedTransaction(
    entityId: string,
    entityType: ENTITY_TYPE,
    txnId: string
  ): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readwrite')
      const store = transaction.objectStore(this.transactionStoreName)
      transaction.oncomplete = () => resolve()
      transaction.onabort = () => reject(transaction.error || new Error('transaction aborted'))
      transaction.onerror = () => reject(transaction.error)

      const getReq = store.get(this.entityKey(entityId, entityType))
      getReq.onsuccess = () => {
        const record = getReq.result as IEntityTransactionsRecord | undefined
        if (!record || !Array.isArray(record.transactions)) {
          return
        }
        const now = Math.floor(Date.now() / 1000)
        let changed = false
        const transactions = record.transactions.map((t: any) => {
          if (t.id === txnId && !t.deleted) {
            changed = true
            return { ...t, deleted: true, delete_time: now }
          }
          return t
        })
        if (changed) {
          store.put({ ...record, transactions, lastUpdated: Date.now() })
        }
      }
    })
  }

  async deleteTransactionsByEntity(entityId: string, entityType: ENTITY_TYPE): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readwrite')
      const store = transaction.objectStore(this.transactionStoreName)
      const request = store.delete(this.entityKey(entityId, entityType))

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clearAll(): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.transactionStoreName], 'readwrite')
      const store = transaction.objectStore(this.transactionStoreName)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // Metadata methods
  async getMetadata(
    entityId: string,
    entityType: ENTITY_TYPE
  ): Promise<ITransactionMetadata | null> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    // Check if metadata store exists
    if (!this.db.objectStoreNames.contains(this.metadataStoreName)) {
      console.warn('[DBManager] Metadata store does not exist, returning null')
      return null
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.metadataStoreName], 'readonly')
      const store = transaction.objectStore(this.metadataStoreName)
      const key = `${entityId}_${entityType}`
      const request = store.get(key)

      request.onsuccess = () => {
        resolve(request.result || null)
      }

      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  async updateMetadata(metadata: ITransactionMetadata): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    // Check if metadata store exists
    if (!this.db.objectStoreNames.contains(this.metadataStoreName)) {
      console.warn('[DBManager] Metadata store does not exist, skipping update')
      return
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.metadataStoreName], 'readwrite')
      const store = transaction.objectStore(this.metadataStoreName)

      const key = `${metadata.entityId}_${metadata.entityType}`
      const dataToStore = {
        ...metadata,
        entityId_entityType: key,
        updatedAt: Date.now()
      }

      const request = store.put(dataToStore)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async createMetadata(entityId: string, entityType: ENTITY_TYPE): Promise<ITransactionMetadata> {
    const now = Date.now()
    const metadata: ITransactionMetadata = {
      entityId,
      entityType,
      lastRecentSync: 0,
      lastHistoricalSync: 0,
      totalTransactions: 0,
      recentTransactionCount: 0,
      historicalTransactionCount: 0,
      lastTransactionTimestamp: 0,
      hasCompletedFullFetch: false,
      createdAt: now,
      updatedAt: now
    }

    await this.updateMetadata(metadata)
    return metadata
  }

  async updateSyncTimes(
    entityId: string,
    entityType: ENTITY_TYPE,
    recentSync?: number,
    historicalSync?: number
  ): Promise<void> {
    const metadata =
      (await this.getMetadata(entityId, entityType)) ||
      (await this.createMetadata(entityId, entityType))

    if (recentSync !== undefined) {
      metadata.lastRecentSync = recentSync
    }
    if (historicalSync !== undefined) {
      metadata.lastHistoricalSync = historicalSync
    }

    await this.updateMetadata(metadata)
  }

  async updateTransactionCounts(
    entityId: string,
    entityType: ENTITY_TYPE,
    total: number,
    recent: number,
    historical: number
  ): Promise<void> {
    const metadata =
      (await this.getMetadata(entityId, entityType)) ||
      (await this.createMetadata(entityId, entityType))

    metadata.totalTransactions = total
    metadata.recentTransactionCount = recent
    metadata.historicalTransactionCount = historical

    await this.updateMetadata(metadata)
  }

  async updateLastTransactionTimestamp(
    entityId: string,
    entityType: ENTITY_TYPE,
    timestamp: number
  ): Promise<void> {
    const metadata =
      (await this.getMetadata(entityId, entityType)) ||
      (await this.createMetadata(entityId, entityType))

    metadata.lastTransactionTimestamp = timestamp
    await this.updateMetadata(metadata)
  }

  async setFullFetchCompleted(
    entityId: string,
    entityType: ENTITY_TYPE,
    completed: boolean = true
  ): Promise<void> {
    const metadata =
      (await this.getMetadata(entityId, entityType)) ||
      (await this.createMetadata(entityId, entityType))

    metadata.hasCompletedFullFetch = completed
    await this.updateMetadata(metadata)
  }

  /**
   * Batch-write `hasCompletedFullFetch = true` metadata rows for many accounts in
   * a few transactions (the seed marks thousands of accounts at once). Overwrites
   * any existing row for the account — the seed is the authoritative full fetch.
   */
  async bulkSetFullFetchCompleted(entityIds: string[], entityType: ENTITY_TYPE): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    if (!this.db.objectStoreNames.contains(this.metadataStoreName)) {
      return
    }
    const batchSize = 10000
    for (let start = 0; start < entityIds.length; start += batchSize) {
      const batch = entityIds.slice(start, start + batchSize)
      const now = Date.now()
      await new Promise<void>((resolve, reject) => {
        const transaction = this.db!.transaction([this.metadataStoreName], 'readwrite')
        const store = transaction.objectStore(this.metadataStoreName)
        transaction.oncomplete = () => resolve()
        transaction.onabort = () => reject(transaction.error || new Error('transaction aborted'))
        transaction.onerror = () => reject(transaction.error)

        for (const entityId of batch) {
          store.put({
            entityId,
            entityType,
            entityId_entityType: `${entityId}_${entityType}`,
            lastRecentSync: 0,
            lastHistoricalSync: 0,
            totalTransactions: 0,
            recentTransactionCount: 0,
            historicalTransactionCount: 0,
            lastTransactionTimestamp: 0,
            hasCompletedFullFetch: true,
            createdAt: now,
            updatedAt: now
          })
        }
      })
    }
  }

  async clearMetadata(entityId: string, entityType: ENTITY_TYPE): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.metadataStoreName], 'readwrite')
      const store = transaction.objectStore(this.metadataStoreName)
      const key = `${entityId}_${entityType}`
      const request = store.delete(key)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clearAllMetadata(): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.metadataStoreName], 'readwrite')
      const store = transaction.objectStore(this.metadataStoreName)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  // --- Seed metadata (per-business seed flag) ---

  /** Read the seed record for a business, or null if never seeded / store absent. */
  async getSeedInfo(businessId: string): Promise<ISeedInfo | null> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    if (!this.db.objectStoreNames.contains(this.seedMetadataStoreName)) {
      return null
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.seedMetadataStoreName], 'readonly')
      const store = transaction.objectStore(this.seedMetadataStoreName)
      const request = store.get(businessId)

      request.onsuccess = () => {
        resolve((request.result as ISeedInfo) || null)
      }
      request.onerror = () => {
        reject(request.error)
      }
    })
  }

  /** Write/replace the seed record for a business. */
  async setSeedInfo(
    businessId: string,
    info: { seedVersion: number; seededAt: number; completedAccountTypes?: number[] }
  ): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    if (!this.db.objectStoreNames.contains(this.seedMetadataStoreName)) {
      console.warn('[DBManager] Seed metadata store does not exist, skipping setSeedInfo')
      return
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.seedMetadataStoreName], 'readwrite')
      const store = transaction.objectStore(this.seedMetadataStoreName)
      const record: ISeedInfo = {
        businessId,
        seedVersion: info.seedVersion,
        seededAt: info.seededAt,
        completedAccountTypes: info.completedAccountTypes || []
      }
      const request = store.put(record)

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clearAllSeedInfo(): Promise<void> {
    if (!this.db) {
      throw new Error('Database not initialized')
    }
    if (!this.db.objectStoreNames.contains(this.seedMetadataStoreName)) {
      return
    }

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.seedMetadataStoreName], 'readwrite')
      const store = transaction.objectStore(this.seedMetadataStoreName)
      const request = store.clear()

      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  }

  async clearAllData(): Promise<void> {
    // Open the DB if it wasn't already this session, otherwise clearAll() throws
    // "Database not initialized" (e.g. logging out without viewing any ledger).
    if (!this.db) {
      await this.init()
    }
    await this.clearAll()
    await this.clearAllMetadata()
    // Reset seed flags on signout so the next user re-seeds from scratch.
    await this.clearAllSeedInfo()
  }
}

// Singleton instance
let databaseManagerInstance: TransactionDatabaseManager | null = null

export const getTransactionDatabaseManager = (): TransactionDatabaseManager => {
  if (!databaseManagerInstance) {
    databaseManagerInstance = new TransactionDatabaseManager()
  }
  return databaseManagerInstance
}

export default TransactionDatabaseManager
